import base from "../catalogues/en/base.json" with { type: "json" };
import type shell from "../catalogues/en/shell.json";
import type pickers from "../catalogues/en/pickers.json";
import type markdown from "../catalogues/en/markdown.json";
import type collection from "../catalogues/en/collection.json";
import type item from "../catalogues/en/item.json";
import type project from "../catalogues/en/project.json";
import type inbox from "../catalogues/en/inbox.json";
import type viewsNav from "../catalogues/en/viewsNav.json";
import type bulkReport from "../catalogues/en/bulkReport.json";
import type cycleArea from "../catalogues/en/cycle.json";
import type projectSettingsArea from "../catalogues/en/projectSettings.json";
import type boardArea from "../catalogues/en/board.json";
import type adminArea from "../catalogues/en/admin.json";
import type moduleArea from "../catalogues/en/module.json";
import type itemPanelsArea from "../catalogues/en/itemPanels.json";
import type viewsArea from "../catalogues/en/views.json";
import type bulkArea from "../catalogues/en/bulk.json";
import type searchArea from "../catalogues/en/search.json";
import type activityArea from "../catalogues/en/activity.json";
import type desktopArea from "../catalogues/en/desktop.json";
import type themeArea from "../catalogues/en/theme.json";
import type timelineArea from "../catalogues/en/timeline.json";
import type calendarArea from "../catalogues/en/calendar.json";
import type tableArea from "../catalogues/en/table.json";
import type accountArea from "../catalogues/en/account.json";
import type fieldArea from "../catalogues/en/field.json";
import type integrationArea from "../catalogues/en/integration.json";
import type m365Area from "../catalogues/en/m365.json";
import type meetingArea from "../catalogues/en/meeting.json";
import type aiArea from "../catalogues/en/ai.json";
import type dashboardArea from "../catalogues/en/dashboard.json";
import type verifyArea from "../catalogues/en/verify.json";
import type setupArea from "../catalogues/en/setup.json";
import type welcomeArea from "../catalogues/en/welcome.json";
import type workflowArea from "../catalogues/en/workflow.json";
import type teamsArea from "../catalogues/en/teams.json";
import type assistantArea from "../catalogues/en/assistant.json";
import type portalArea from "../catalogues/en/portal.json";

/**
 * The catalogue mechanism of `research.md` W-17 and FR-015-131. Feature 014 owns the
 * contents and the locale list; this module owns how a catalogue is found, loaded and
 * fallen back on. English is the default locale and the fallback for every missing key, so
 * a string is never rendered as its key (`spec.md` § Edge cases).
 *
 * Catalogues are split by area, one JSON file each so the areas are authored apart. Three
 * entries draw from them (FR-015-120): the application, the sign-in pages and the first-run setup.
 * `base.json` holds the words the sign-in and setup entries draw, exactly those and no others,
 * and is the one catalogue this module imports, so it is all those two entries carry. The areas
 * of the application's initial chunk (`research.md` W-26: the shell, the collection machinery,
 * the pickers, the item detail, the project screens, the inbox and the markdown surfaces) are
 * imported statically by `app.ts`, which the application's entry hands to
 * `registerAppCatalogue` before it renders, so they count against the application's budget and
 * never against the other two (2026-09-28: they had grown the sign-in pages past theirs). A later
 * area is a lazy import registered beside the route that needs it, so it is code-split with that
 * route. `bun run i18n:areas` holds `base.json` to the keys the two entries read, and
 * `bun run i18n:bundle` fails when an application word reaches either entry's bundle.
 *
 * A lazily loaded area's keys are typed through a type-only import of its English file, which
 * the build erases, so `t()` checks an area key without the area's strings entering the
 * initial chunk. Its English loader is declared with `lazyArea` by the area's own route module,
 * inside the route's lazy chunk, as a dynamic `import()` of the JSON file this package exports.
 * Declaring it here instead would put the dynamic import, and the bundler's preload helper with
 * it, into the chunk the public entry shares with the application. `AreaCatalogue`
 * (`provider.tsx`) loads a declared area beneath the screens that read it.
 */

/** The catalogue every entry carries: the words the sign-in and setup pages draw. */
export const shellCatalogue: Readonly<Record<keyof typeof base, string>> = base;

/** The keys of the application's static catalogues, which `app.ts` carries and the application registers. */
type AppKey = keyof typeof shell | keyof typeof pickers | keyof typeof markdown | keyof typeof collection | keyof typeof item | keyof typeof project | keyof typeof inbox | keyof typeof viewsNav | keyof typeof bulkReport;

let appCatalogue: Catalogue = {};

/**
 * Register the application's static catalogues (`app.ts`), merged beneath every catalogue the
 * application loads. Only the application's entry calls it, so the words it holds stay out of the
 * sign-in and setup entries.
 */
export function registerAppCatalogue(messages: Catalogue): void {
  appCatalogue = messages;
}

/** The catalogue beneath every locale and area: the entries' words, then the application's once registered. */
export function baseMessages(): Catalogue {
  return { ...shellCatalogue, ...appCatalogue };
}

/** The names of the areas loaded on demand rather than with the shell. */
export type LazyArea = "cycle" | "projectSettings" | "board" | "admin" | "module" | "itemPanels" | "views" | "bulk" | "search" | "activity" | "desktop" | "theme" | "timeline" | "calendar" | "table" | "account" | "field" | "integration" | "m365" | "meeting" | "ai" | "dashboard" | "verify" | "setup" | "welcome" | "workflow" | "teams" | "assistant" | "portal";

export type MessageKey = keyof typeof base | AppKey | keyof typeof cycleArea | keyof typeof projectSettingsArea | keyof typeof boardArea | keyof typeof adminArea | keyof typeof moduleArea | keyof typeof itemPanelsArea | keyof typeof viewsArea | keyof typeof bulkArea | keyof typeof searchArea | keyof typeof activityArea | keyof typeof desktopArea | keyof typeof themeArea | keyof typeof timelineArea | keyof typeof calendarArea | keyof typeof tableArea | keyof typeof accountArea | keyof typeof fieldArea | keyof typeof integrationArea | keyof typeof m365Area | keyof typeof meetingArea | keyof typeof aiArea | keyof typeof dashboardArea | keyof typeof verifyArea | keyof typeof setupArea | keyof typeof welcomeArea | keyof typeof workflowArea | keyof typeof teamsArea | keyof typeof assistantArea | keyof typeof portalArea;

/** The English message a key formats when the active catalogue lacks it: the base's or the application's, for one of theirs. */
export function englishShellMessage(key: MessageKey): string | undefined {
  return (shellCatalogue as Catalogue)[key] ?? appCatalogue[key];
}

/** A catalogue for one locale and one area: keys to ICU messages. An area catalogue carries
 * its own keys beside the shell's, so the map is open; `t()` is typed against `MessageKey`. */
export type Catalogue = Readonly<Record<string, string>>;

export type CatalogueLoader = () => Promise<Catalogue>;

export const defaultLocale = "en";

/** Name a catalogue key outside a render, for a module that hands keys to a component
 * (`@baton/core`'s transition refusals). The extraction script counts these uses like `t()`. */
export function messageKey<K extends MessageKey>(key: K): K {
  return key;
}

/** The registered loaders, keyed by locale then area. English's shell area is built in. */
const loaders = new Map<string, Map<string, CatalogueLoader>>([
  [
    "en",
    new Map<string, CatalogueLoader>([
      ["shell", () => Promise.resolve(shellCatalogue)],
    ]),
  ],
]);

/** Register the loader for one locale and area; feature 014 calls this per translated
 * area, and a route module calls it for the area it code-splits. */
export function registerCatalogue(locale: string, area: string, loader: CatalogueLoader): void {
  const byArea = loaders.get(locale) ?? new Map<string, CatalogueLoader>();
  byArea.set(area, loader);
  loaders.set(locale, byArea);
}

declare const declared: unique symbol;

/** A lazy area whose English loader is registered. Only `lazyArea` makes one, so an
 * `AreaCatalogue` can never be handed an area whose loader nothing registered. */
export type Area = LazyArea & { readonly [declared]: true };

/**
 * Declare a lazy area: register its English loader and return its name as the `Area` an
 * `AreaCatalogue` takes. The registration is the value a screen passes rather than a statement
 * beside it, so a bundler can drop it only by dropping the screen. 2026-09-28: the area modules
 * used to export the name as a string constant with the registration as a side effect of the
 * module; the application package declares its modules free of side effects, and when two
 * chunks read the `ai` area the bundler inlined the constant into both and dropped the module,
 * registration and all, so the Connected apps and AI settings pages rendered their keys.
 */
export function lazyArea(area: LazyArea, english: CatalogueLoader): Area {
  registerCatalogue(defaultLocale, area, english);
  return area as Area;
}

/** The locales with at least one registered catalogue. */
export function availableLocales(): string[] {
  return [...loaders.keys()];
}

/**
 * Load one area for a locale, resolving the locale as BCP 47 does (`de-AT` falls back to
 * `de`, then to English). The English area is always loaded beneath it so a key the locale
 * lacks renders in English rather than as its key, and an area with no English loader is an
 * error rather than an empty catalogue, which would render every one of its keys.
 */
export async function loadCatalogue(locale: string, area = "shell"): Promise<Catalogue> {
  const english = loaders.get(defaultLocale)?.get(area);
  if (english === undefined) throw new Error(`no English catalogue is registered for the area "${area}"`);
  const base = await english();
  for (const candidate of candidates(locale)) {
    const loader = loaders.get(candidate)?.get(area);
    if (loader) return { ...base, ...(await loader()) };
  }
  return base;
}

/** `de-AT-u-co-phonebk` → `de-AT`, `de`. */
export function candidates(locale: string): string[] {
  const tag = locale.split("-u-")[0] ?? locale;
  const parts = tag.split("-");
  const out: string[] = [];
  for (let i = parts.length; i > 0; i -= 1) out.push(parts.slice(0, i).join("-"));
  return out;
}

/** A load that records how it settled, in the `status`, `value` and `reason` fields React's `use()` reads, so a settled load is read without suspending. */
type AreaLoad = Promise<Catalogue> & { status?: "fulfilled" | "rejected"; value?: Catalogue; reason?: unknown };

const areaLoads = new Map<string, AreaLoad>();

/** One load per locale and area for the life of the page; a failed load is forgotten so a retry loads again. */
export function loadAreaOnce(locale: string, area: Area): Promise<Catalogue> {
  const key = `${locale}:${area}`;
  const existing = areaLoads.get(key);
  if (existing !== undefined) return existing;
  const load: AreaLoad = loadCatalogue(locale, area);
  areaLoads.set(key, load);
  load.then(
    (catalogue) => {
      load.status = "fulfilled";
      load.value = catalogue;
    },
    (reason: unknown) => {
      load.status = "rejected";
      load.reason = reason;
      areaLoads.delete(key);
    },
  );
  return load;
}
